import { logger } from "@pufflig/logger";
import { v2 } from "@pufflig/prompt-studio-types";
import { getQueryContext, safeStringify } from "@pufflig/prompt-tools";
import { getRunGraph } from "./getRunGraph";
import { CreateRunbookOptions } from "./types";

export const getRunbookResources = (steps: v2.QueryStep[], resources: Record<string, v2.Resource>) => {
  const runbookResources = new Set<v2.Resource>();

  for (const stepId in steps) {
    const step = steps[stepId];
    step.references.forEach((ref) => {
      if (ref.type === "resource") {
        const resource = resources[ref.sourceId];
        if (resource) {
          runbookResources.add(resource);
        } else {
          logger.error({ stepId, ref }, "Resource not found");
        }
      }
    });
  }

  return runbookResources;
};

/**
 * This function preloads all the resources needed and a runnable definition of the promptbook.
 * The runbook is a graph of steps that will be run in order.
 *
 * @param promptbook
 * @param options
 * @returns
 */
export const createRunbook = async (promptbook: v2.PromptbookState, options?: CreateRunbookOptions) => {
  const { steps, selectedVersions, resources, externalResources } = promptbook;
  const stepsWithResults = new Set(Object.keys(selectedVersions));

  const runbook = getRunGraph(steps, stepsWithResults, options);
  const runbookSteps = Object.values(steps).filter((step) => runbook.steps[step.stepId]);

  // for the steps that will be run, retrieve the resources
  const runbookResources = getRunbookResources(runbookSteps, { ...resources, ...externalResources });

  // add all the resources to the context of each runbook step
  for (const [stepId, step] of Object.entries(runbook.steps)) {
    const { references } = steps[stepId];
    const relevantResources = Array.from(runbookResources).filter((r) =>
      references.some((ref) => ref.sourceId === r.resourceId),
    );
    const context = await getQueryContext(relevantResources);

    step.args.context = safeStringify(context);

    logger.debug({ context: step.args.context }, "Step Context Ready");
  }

  return { runbook, resources: Array.from(runbookResources) };
};
